import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { BsAward, BsTrophy, BsPeople, BsStar } from "react-icons/bs";

gsap.registerPlugin(ScrollTrigger);

const ExtracurricularAchievements = () => {
  useEffect(() => {
    gsap.delayedCall(1, () => {
      // Animate section heading
      gsap.timeline({
        scrollTrigger: {
          trigger: "#extra-achieve-section",
          start: "top 80%",
          toggleActions: "play none resume pause",
        },
      }).fromTo(
        "#extra-heading",
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, ease: "power3.out", duration: 1.2 }
      );

      // Animate leadership cards
      const leadershipCards = document.querySelectorAll(".leadership-card");
      leadershipCards.forEach((card) => {
        gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none resume pause",
          },
        }).from(card, {
          y: 40,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
        });
      });

      // Animate achievement cards with stagger
      const achievementCards = document.querySelectorAll(".achievement-card");
      gsap.timeline({
        scrollTrigger: {
          trigger: ".achievements-grid",
          start: "top 85%",
          toggleActions: "play none resume pause",
        },
      }).from(achievementCards, {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.12,
      });

      // Animate best paper award
      gsap.timeline({
        scrollTrigger: {
          trigger: ".best-paper-card",
          start: "top 85%",
          toggleActions: "play none resume pause",
        },
      }).from(".best-paper-card", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      });
    });
  }, []);

  const leadershipRoles = [
    {
      role: "Student Placement Coordinator",
      department: "Data Science, B.Tech",
      institution: "Buddha Institute of Technology",
      location: "Gorakhpur, Uttar Pradesh",
      period: "September 2024 – Present",
      responsibilities: [
        "Coordinate placement activities for Data Science students, facilitating communication between recruiters and candidates",
        "Organize campus recruitment drives, technical workshops, and industry networking events",
      ],
      gradient: "from-blue-500 to-indigo-600",
      icon: BsPeople,
    },
    {
      role: "Class Representative",
      department: "Data Science, B.Tech",
      institution: "Buddha Institute of Technology",
      location: "Gorakhpur, Uttar Pradesh",
      period: "April 2024 – Present",
      responsibilities: [
        "Facilitate communication between students and faculty to address academic concerns and improve learning outcomes",
        "Coordinate class activities, technical workshops, and collaborative learning initiatives",
        "Provide academic guidance and mentorship to junior students",
      ],
      gradient: "from-purple-500 to-pink-500",
      icon: BsStar,
    },
  ];

const achievements = [
    {
      title: "Winner: SPARKATHON",
      description: "AI-Driven Personalized Medicine and Smart Agriculture",
      organizer: "Buddha Innovation Council, BIC",
      year: "2025",
      gradient: "from-purple-400 to-pink-500",
      position: "1st Place",
    },
    {
      title: "Winner: techYuva 8.0",
      description: "Innovative Model Presentation Competition",
      organizer: "Buddha Group of Institutions, Gorakhpur",
      year: "2023",
      gradient: "from-yellow-400 to-orange-500",
      position: "1st Place",
    },
    {
      title: "Winner: techYuva 9.0",
      description: "Innovative Model Presentation Competition",
      organizer: "Buddha Group of Institutions, Gorakhpur",
      year: "2024",
      gradient: "from-green-400 to-emerald-500",
      position: "1st Place",
    },
    {
      title: "Runner Up: techYuva 10.0",
      description: "Innovative Model Presentation Competition",
      organizer: "Buddha Group of Institutions, Gorakhpur",
      year: "2025",
      gradient: "from-cyan-400 to-blue-500",
      position: "2nd Place",
    },
  ];

  return (
    <div
      id="extra-achieve-section"
      className="w-full py-20 xl:px-44 lg:px-36 md:px-32 sm:px-28 vvs:px-12 px-10"
    >
      {/* Section Heading */}
      <h1
        id="extra-heading"
        className="text-white/70 font-Glimer-Outlined font-semibold lg:mb-20 mb-16 vvs:text-[4rem] sm:text-[5.2rem] md:text-[6.4rem] lg:text-[7.5rem] xl:text-[9.2rem] 2xl:text-[10.8rem] text-[3.5rem] leading-[3.5rem] sm:leading-[5.5rem] md:leading-[6rem] lg:leading-[7rem] xl:leading-[9rem] 2xl:leading-[10rem] cursor-default"
      >
        Leadership & Recognition
      </h1>

      {/* Leadership Section */}
      <div className="mb-32">
        <div className="flex items-center mb-12 space-x-4">
          <BsPeople className="text-white h-8 w-8 md:h-10 md:w-10" />
          <h2 className="text-white font-Glimer-Bold text-3xl md:text-4xl lg:text-5xl">
            Leadership Experience
          </h2>
        </div>

        <div className="space-y-8 lg:space-y-12">
          {leadershipRoles.map((role, index) => {
            const IconComponent = role.icon;
            return (
              <div
                key={index}
                className="leadership-card relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 lg:p-10 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-white/5"
              >
                {/* Gradient Icon */}
                <div
                  className={`absolute -top-6 -left-6 h-16 w-16 md:h-20 md:w-20 bg-gradient-to-br ${role.gradient} rounded-full flex items-center justify-center shadow-lg`}
                >
                  <IconComponent className="text-white h-8 w-8 md:h-10 md:w-10" />
                </div>

                {/* Content */}
                <div className="ml-12 md:ml-16">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div>
                      <h3 className="text-white font-Glimer-Bold text-xl md:text-2xl lg:text-3xl mb-2">
                        {role.role}
                      </h3>
                      <p className="text-white/80 font-Glimer-Medium text-base md:text-lg mb-1">
                        {role.department}
                      </p>
                      <p className="text-white/70 font-Glimer-Light text-base md:text-lg">
                        {role.institution}
                      </p>
                    </div>
                    <div className="mt-2 md:mt-0 text-left md:text-right">
                      <p className="text-white/60 font-Glimer-Light text-sm md:text-base">
                        {role.period}
                      </p>
                      <p className="text-white/60 font-Glimer-Light text-sm md:text-base">
                        {role.location}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2 mt-4">
                    {role.responsibilities.map((resp, idx) => (
                      <li
                        key={idx}
                        className="text-white/70 font-Glimer-Light text-base md:text-lg leading-relaxed flex items-start"
                      >
                        <span className="text-white/40 mr-2">•</span>
                        <span>{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Achievements Section */}
      <div className="mb-32">
        <div className="flex items-center mb-12 space-x-4">
          <BsTrophy className="text-white h-8 w-8 md:h-10 md:w-10" />
          <h2 className="text-white font-Glimer-Bold text-3xl md:text-4xl lg:text-5xl">
            Achievements
          </h2>
        </div>

        {/* techYuva Achievements Grid */}
        <div className="achievements-grid grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {achievements.map((achievement, index) => (
            <div
              key={index}
              className="achievement-card relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-white/5 overflow-hidden group"
            >
              {/* Gradient Background on Hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${achievement.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
              ></div>

              {/* Content */}
              <div className="relative z-10">
                <div className={`inline-block px-3 py-1 mb-4 rounded-full bg-gradient-to-r ${achievement.gradient} text-white font-Glimer-Medium text-sm`}>
                  {achievement.position}
                </div>
                <h3 className="text-white font-Glimer-Bold text-xl md:text-2xl mb-3">
                  {achievement.title}
                </h3>
                <p className="text-white/70 font-Glimer-Light text-sm md:text-base mb-2">
                  {achievement.description}
                </p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <p className="text-white/50 font-Glimer-Light text-xs md:text-sm">
                    {achievement.organizer}
                  </p>
                  <p className="text-white/50 font-Glimer-Light text-xs md:text-sm">
                    {achievement.year}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Best Paper Award - Featured Card */}
        <div className="best-paper-card relative bg-gradient-to-br from-amber-500/10 via-yellow-500/10 to-orange-500/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-amber-500/20 hover:border-amber-500/40 transition-all duration-300 shadow-xl overflow-hidden">
          {/* Content */}
          <div className="relative z-10">
            <div className="flex items-start space-x-6 mb-6">
              <div className="h-16 w-16 md:h-20 md:w-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <BsAward className="text-white h-8 w-8 md:h-10 md:w-10" />
              </div>
              <div className="flex-1">
                <div className="inline-block px-3 py-1 mb-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white font-Glimer-Medium text-sm">
                  Best Paper Award
                </div>
                <h3 className="text-white font-Glimer-Bold text-2xl md:text-3xl lg:text-4xl mb-2">
                  BLUEGEN
                </h3>
                <p className="text-amber-300/90 font-Glimer-Medium text-base md:text-lg">
                  RTSET-2025 National Conference
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-white/80 font-Glimer-Light text-base md:text-lg leading-relaxed">
                Recognized with the Best Paper Award at the 5th National Conference on Recent Trends in Science, Engineering and Technology for the research paper titled <span className="text-amber-300 font-Glimer-Medium">"BLUEGEN"</span>.
              </p>

              <div className="bg-white/5 rounded-xl p-4 md:p-6 border border-white/10">
                <p className="text-white/60 font-Glimer-Light text-sm md:text-base mb-2">
                  Co-authors
                </p>
                <p className="text-white/80 font-Glimer-Medium text-sm md:text-base">
                  Alok Kumar Mishra, Sarthak Suvigya Singh
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtracurricularAchievements;